import { LinearProgress, Typography } from '@mui/material';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { Card, Flex, FlexColumn, Link } from '@elementary/common-ui/components';
import { numberWithCommas } from '@elementary/common-ui/utils/common';
import { fontSize, fontWeight } from '@elementary/common-ui/style/utils';

const SUPPORT_MAILTO = 'mailto:support@elementary-data.com';

type Props = {
  title: string;
  subtitle: string;
  creditsUsed: number;
  creditLimit: number | null;
  resetsText: string;
  limitReachedText: string;
};

const LimitReachedChip = ({ text }: { text: string }) => (
  <Flex
    sx={{
      alignItems: 'center',
      gap: 1,
      px: 1.5,
      py: 0.75,
      borderRadius: 1.5,
      backgroundColor: 'status.warning.bg',
    }}
  >
    <ErrorOutlineRoundedIcon
      sx={{ fontSize: fontSize('xl'), color: 'status.warning.strong' }}
    />
    <Typography sx={{ fontSize: fontSize('base') }}>
      {text} <Link href={SUPPORT_MAILTO}>Contact us.</Link>
    </Typography>
  </Flex>
);

const ResetsAt = ({ text }: { text: string }) => (
  <Typography sx={{ fontSize: fontSize('md'), color: 'content.secondary' }}>
    {text}
  </Typography>
);

const Heading = ({ title, subtitle }: { title: string; subtitle: string }) => (
  <FlexColumn sx={{ gap: 0.25 }}>
    <Typography
      sx={{ fontWeight: fontWeight('semibold'), fontSize: fontSize('lg') }}
    >
      {title}
    </Typography>
    <Typography sx={{ fontSize: fontSize('md'), color: 'content.secondary' }}>
      {subtitle}
    </Typography>
  </FlexColumn>
);

const CreditsCard = ({
  title,
  subtitle,
  creditsUsed,
  creditLimit,
  resetsText,
  limitReachedText,
}: Props) => {
  const used = Math.round(creditsUsed);

  if (creditLimit === null) {
    return (
      <Card>
        <FlexColumn sx={{ gap: 1.5, width: '100%' }}>
          <Heading title={title} subtitle={subtitle} />
          <Typography
            sx={{
              fontWeight: fontWeight('semibold'),
              fontSize: fontSize('display'),
            }}
          >
            {numberWithCommas(used)}
          </Typography>
          <ResetsAt text={resetsText} />
        </FlexColumn>
      </Card>
    );
  }

  const pct =
    creditLimit > 0
      ? Math.min(100, Math.round((used / creditLimit) * 100))
      : 100;
  const isLimitReached = used >= creditLimit;

  return (
    <Card>
      <FlexColumn sx={{ gap: 1.5, width: '100%' }}>
        <Heading title={title} subtitle={subtitle} />
        <Flex sx={{ alignItems: 'center', gap: 2 }}>
          <Flex sx={{ alignItems: 'baseline', gap: 1 }}>
            <Typography
              sx={{
                fontWeight: fontWeight('semibold'),
                fontSize: fontSize('display'),
              }}
            >
              {pct}%
            </Typography>
            <Typography
              sx={{ fontSize: fontSize('display'), color: 'content.secondary' }}
            >
              {numberWithCommas(used)} / {numberWithCommas(creditLimit)}
            </Typography>
          </Flex>
          {isLimitReached && <LimitReachedChip text={limitReachedText} />}
        </Flex>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{ height: 8, borderRadius: 4 }}
        />
        <ResetsAt text={resetsText} />
      </FlexColumn>
    </Card>
  );
};

export default CreditsCard;
